import { EntityRepository } from '@record-it-npm/synchro-client';
import { useEffect, useMemo } from 'react';
import { useRefWithCurrentValue } from 'record-it-components/hooks/useRefWithCurrentValue';
import { EntityName } from 'record-it-time-common/EntityName';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import {
  RateLimit,
  RateLimitedFunction
} from 'record-it-utils/utils/RateLimit/RateLimit';
import { Utils } from 'record-it-utils/utils/Utils/Utils';
import { AppEntityManager } from '../../../classes/entities/AppEntityManager';
import { useEntityManager } from '../useEntityManager';
import { UseEntitiesChangedListenerUtils } from './UseEntitiesChangedListenerUtils';

export function useEntitiesChangedListener(
  entityNames: Array<EntityName>,
  callback: () => void
): void {
  const callbackRef = useRefWithCurrentValue(callback);
  const { entityManager } = useEntityManager();

  const rateLimitedCallback = useMemo(() => {
    return RateLimit.rateLimitFunction(() => {
      callbackRef.current();
    }, UseEntitiesChangedListenerUtils.rateLimitTimeout);
  }, [callbackRef]);

  const disposableMap = useMemo(() => {
    // we need the entityManager and the rateLimitedCallback as a dependency here, so the disposableMap won't contain references to the old instances.
    // It's passed into noop so eslint will think that this is a necessary dependency for useMemo
    Utils.noop(entityManager);
    Utils.noop(rateLimitedCallback);

    return new Map<EntityName, Disposable>();
  }, [entityManager, rateLimitedCallback]);

  for (const entityName of entityNames) {
    const existingDisposable = disposableMap.get(entityName);
    if (!existingDisposable) {
      disposableMap.set(
        entityName,
        registerListener({
          entityManager,
          entityName,
          rateLimitedCallback
        })
      );
    }
  }

  useEffect(() => {
    return () => {
      for (const [entityName, disposable] of disposableMap.entries()) {
        disposable.dispose();
        disposableMap.delete(entityName);
      }

      rateLimitedCallback.cancel();
    };
  }, [disposableMap, rateLimitedCallback]);
}

function registerListener({
  entityManager,
  entityName,
  rateLimitedCallback
}: {
  entityManager: AppEntityManager;
  entityName: EntityName;
  rateLimitedCallback: RateLimitedCallback;
}): Disposable {
  const repository = entityManager.entityRepositoryContainer.getByEntityName(
    entityName
  ) as EntityRepository<any, any, any>;

  return repository.registerHooks({
    afterEntityCreated: rateLimitedCallback,
    afterEntityDeleted: rateLimitedCallback,
    afterEntityUpdated: rateLimitedCallback,
    afterEntityRemovedLocally: rateLimitedCallback,
    afterEntityUpdatedLocally: rateLimitedCallback
  });
}

type Callback = () => void;
type RateLimitedCallback = RateLimitedFunction<Callback>;
